import {
	Box,
	Chip,
	Divider,
	Grid,
	IconButton,
	Paper,
	Skeleton,
	styled,
	Table,
	TableBody,
	TableCell,
	tableCellClasses,
	TableContainer,
	TableHead,
	TableRow,
	Typography,
} from '@mui/material';
import { dateConverter } from '../../Utils/Helpers';
import { useEffect, useState } from 'react';
import { StarIcon, ViewELIcon } from '../../assets/icons/Icons';
import { MAIN_THEME_COLOR, WHITE } from '../../assets/theme/Colors';
import {
	fetch360DashboardData,
	fetchCarePlanById,
	fetchExternalElderPlan,
} from '../../services/ElderService';
import { getAccessToken, getElderHomeId } from '../../Utils/AuthUtil';
import PlanOverview from './PlanOverview';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';

function AggressionWidget({ data }) {
	return (
		<Paper
			elevation={0}
			sx={{
				width: '100%',
				height: 200,
				borderRadius: '2px',
				overflow: 'hidden',
			}}>
			<Box
				sx={{
					display: 'flex',
					justifyContent: 'space-between',
					padding: '10px',
					backgroundColor: '#f5f5f5',
				}}>
				<Typography
					color={MAIN_THEME_COLOR}
					fontSize={14}
					fontWeight={600}>
					Name
				</Typography>
				<Typography
					color={MAIN_THEME_COLOR}
					fontSize={14}
					fontWeight={600}>
					Scale
				</Typography>
			</Box>
			<Divider />
			<Box
				sx={{
					height: 'calc(100% - 41px)',
					overflowY: 'auto',
				}}>
				{data.map((user, index) => (
					<Box key={user.id ?? index}>
						<Box
							sx={{
								display: 'flex',
								justifyContent: 'space-between',
								alignItems: 'center',
								padding: '10px',
							}}>
							<Typography
								fontSize={12}
								sx={{
									maxWidth: '70%',
									whiteSpace: 'nowrap',
									overflow: 'hidden',
									textOverflow: 'ellipsis',
									fontWeight: '500',
								}}>
								{user.name}
							</Typography>
							<Chip
								label={user.scale}
								size="small"
								sx={{
									fontSize: '12px',
									height: '25px',
									width: '25px',
									borderRadius: 10,
									backgroundColor: 'rgba(97, 97, 97, 0.08)',
									color: MAIN_THEME_COLOR,
									fontWeight: '500',
								}}
							/>
						</Box>
						{index < data.length - 1 && (
							<Divider sx={{ mx: 1.5 }} />
						)}
					</Box>
				))}
			</Box>
		</Paper>
	);
}

function Resident360Dashboard() {
	const token = getAccessToken();
	const homeId = getElderHomeId();
	const navigate = useNavigate();
	const [residentCount, setResidentCount] = useState(0);
	const [residentSummary, setResidentSummary] = useState([]);
	const [isLoading, setIsLoading] = useState(false);
	const [rowsPerPage, setRowsPerPage] = useState(5);
	const [page, setPage] = useState(0);
	const [viewType, setViewType] = useState(null);
	const [planTemplate, setPlanTemplate] = useState('Tile');
	const [selectedElder, setSelectedElder] = useState('');
	const [selectedPlanData, setSelectedPlanData] = useState([]);
	const [selectedPlanType, setSelectedPlanType] = useState('');
	const [expandedAccordion, setExpandedAccordion] = useState(null);
	const [residentBehaviorLevel, setResidentBehaviorLevel] = useState({});

	useEffect(() => {
		fetchDashboardData();
	}, []);

	async function fetchDashboardData() {
		setIsLoading(true);
		const response = await fetch360DashboardData(homeId, token);
		if (!response.error) {
			setResidentSummary(response.tableData);
			setResidentCount(
				response.totalResident ? response.totalResident : 0,
			);
			setResidentBehaviorLevel(response.questionCategory);
		} else {
			toast.error(response.message);
		}
		setIsLoading(false);
	}

	function covertArrayToBulletString(array) {
		if (array.length === 0) {
			return 'No latest reviews';
		}
		return array.map(item => `• ${item}`).join('\n');
	}

	async function onOptimizePlan(
		elderId,
		elderName,
		id,
		exPlanId,
		isExternalElder,
	) {
		navigate('/carePlan', {
			state: {
				elderId,
				id,
				elderName,
				exPlanId: exPlanId && exPlanId !== '' ? exPlanId : null,
				isExternalElder,
				fromDashboard: true,
			},
		});
	}

	async function onViewCurrentPlan(id, elderName, isPlanExternal) {
		let response;
		if (isPlanExternal) {
			response = await fetchExternalElderPlan(id, token);
		} else {
			response = await fetchCarePlanById(id, token, 'initial');
		}

		if (!response.error) {
			setSelectedPlanData(
				isPlanExternal ? response.carePlan.plan : response.plan,
			);
			setViewType('plan');
			// setPlanTemplate(isPlanExternal ? 'Tile' : null);
			setPlanTemplate('Tile');
			setSelectedElder(elderName);
		} else {
			toast.error(response.message);
		}
	}

	const handleChangePage = (event, newPage) => {
		setPage(newPage);
	};

	const handleChangeRowsPerPage = event => {
		setRowsPerPage(+event.target.value);
		setPage(0);
	};

	const handleAccordionExpansion = id => (_, isExpanded) => {
		setExpandedAccordion(isExpanded ? id : false);
	};

	if (viewType && viewType === 'plan') {
		return (
			<PlanOverview
				planData={selectedPlanData}
				selectedElder={selectedElder}
				planType={selectedPlanType}
				expanded={expandedAccordion}
				handleExpansion={handleAccordionExpansion}
				goBack={() => setViewType(null)}
				planTemplate={planTemplate}
			/>
		);
	} else {
		return (
			<Grid
				container
				sx={{
					marginLeft: '30px',
					marginTop: '16px',
					backgroundColor: '#FFFFFF',
					padding: '10px',
				}}>
				<Grid
					container
					sx={{
						marginTop: '10px',
						padding: '10px',
					}}>
					<Grid
						sx={{
							backgroundColor: '#FCFDFF',
							borderRadius: '6px',
							flexDirection: 'column',
							display: 'flex',
							paddingX: '24px',
							paddingY: '12px',
							border: '0.6px solid #F1F3FA',
						}}>
						<Typography
							color={'#5E5C5C'}
							fontSize={'14px'}
							paddingBottom={'5px'}
							fontWeight={'400'}>
							{'Total Resident'}
						</Typography>
						<Typography
							color={'#324054'}
							fontSize={'18px'}
							paddingY={'2.5px'}
							fontWeight={'600'}>
							{residentCount}
						</Typography>
					</Grid>
					{/* <Grid
						sx={{
							backgroundColor: '#FCFDFF',
							borderRadius: '6px',
							flexDirection: 'column',
							display: 'flex',
							paddingX: '24px',
							paddingY: '12px',
							border: '0.6px solid #F1F3FA',
							marginLeft: '10px',
						}}>
						<Typography
							color={'#5E5C5C'}
							fontSize={'14px'}
							paddingBottom={'5px'}
							fontWeight={'400'}>
							{'Care Note'}
						</Typography>
					</Grid> */}
				</Grid>
				{Object.keys(residentBehaviorLevel).length > 0 &&
					Object.keys(residentBehaviorLevel).map((key, index) => (
						<Grid
							key={index}
							sx={{
								width: '25%',
								marginTop: '10px',
								padding: '10px',
							}}>
							<Grid
								sx={{
									backgroundColor: '#FCFDFF',
									borderRadius: '6px',
									flexDirection: 'column',
									display: 'flex',
									paddingX: '14px',
									paddingY: '12px',
									border: '0.6px solid #F1F3FA',
								}}>
								<Typography
									sx={{
										fontSize: '14px',
										fontWeight: '600',
										color: '#000000',
										marginBottom: '10px',
									}}>
									{`Resident ${key.charAt(0).toUpperCase()}${key.slice(1)} Level`}
								</Typography>
								<AggressionWidget
									key={key}
									data={residentBehaviorLevel[key]}
								/>
							</Grid>
						</Grid>
					))}
				<Grid container sx={{ marginTop: '10px', padding: '10px' }}>
					<Typography
						sx={{
							fontSize: '18px',
							fontWeight: '600',
							color: '#000000',
						}}>
						Resident Summary
					</Typography>
					<TableContainer
						component={Paper}
						style={{
							boxShadow: 'none',
							marginTop: '10px',
							border: '1px solid #d4dce4',
						}}>
						<Table
							sx={{
								minWidth: 700,
							}}
							aria-label="customized table">
							<TableHead>
								<TableRow>
									<StyledTableCell align="center">
										<Typography variant="tableHeader">
											Resident
										</Typography>
									</StyledTableCell>
									<StyledTableCell align="center">
										<Typography variant="tableHeader">
											Current Plan
										</Typography>
									</StyledTableCell>
									<StyledTableCell align="center">
										<Typography variant="tableHeader">
											Latest Reviews
										</Typography>
									</StyledTableCell>
									<StyledTableCell align="center">
										<Typography variant="tableHeader">
											Optimize Plan
										</Typography>
									</StyledTableCell>
								</TableRow>
							</TableHead>
							<TableBody>
								{isLoading ? (
									<TableRow>
										{[...Array(4)].map((_, index) => (
											<StyledTableCell
												key={index}
												align="center">
												<Skeleton
													animation="wave"
													style={{
														borderRadius: '5px',
														height: 20,
													}}
													variant="rounded"
												/>
											</StyledTableCell>
										))}
									</TableRow>
								) : residentSummary.length > 0 ? (
									residentSummary.map((item, index) => {
										return (
											<StyledTableRow key={index}>
												<StyledTableCell
													align="center"
													sx={{ width: '25%' }}>
													<Typography variant="tableBody">
														{item.elderId}
													</Typography>
													<br />
													<Typography variant="tableBody">
														{item.name}
													</Typography>
												</StyledTableCell>
												<StyledTableCell
													align="center"
													sx={{ width: '25%' }}>
													<IconButton
														disableRipple
														onClick={() =>
															onViewCurrentPlan(
																item.currentPlanId,
																item.name,
																item.isPlanExternal,
															)
														}>
														<Typography
															sx={{
																fontSize:
																	'12px',
																marginRight:
																	'10px',
																color: '#000000',
															}}>
															{dateConverter(
																item.currentPlanDate,
															)}
														</Typography>
														<ViewELIcon
															color={'#324054'}
														/>
													</IconButton>
												</StyledTableCell>
												<StyledTableCell align="left">
													<Typography
														variant="tableBody"
														sx={{
															whiteSpace:
																'pre-line',
														}}>
														{covertArrayToBulletString(
															item.reviews,
														)}
													</Typography>
												</StyledTableCell>
												<StyledTableCell
													align="center"
													sx={{ width: '10%' }}>
													<IconButton
														disableRipple
														onClick={() =>
															onOptimizePlan(
																item.isExternalElder
																	? item.elderId
																	: item.id,
																item.isExternalElder
																	? item.name
																	: `${item.elderId} (${item.name})`,
																item.id,
																item.externalPlanId,
																item.isExternalElder,
															)
														}
														sx={{
															height: 40,
															width: 40,
															borderRadius: 50,
															backgroundColor:
																MAIN_THEME_COLOR,
														}}>
														<StarIcon
															color={WHITE}
														/>
													</IconButton>
												</StyledTableCell>
											</StyledTableRow>
										);
									})
								) : (
									<StyledTableRow>
										<StyledTableCell
											colSpan={7}
											align="center">
											<Typography variant="tableBody">
												No data available
											</Typography>
										</StyledTableCell>
									</StyledTableRow>
								)}
							</TableBody>
						</Table>
					</TableContainer>
				</Grid>
			</Grid>
		);
	}
}

export const StyledTableCell = styled(TableCell)(({ theme }) => ({
	[`&.${tableCellClasses.head}`]: {
		backgroundColor: '#FFFFFF',
		color: 'rgba(0, 0, 0, 0.5)',
		paddingTop: '20px',
		fontSize: 12,
		fontWeight: 600,
	},
	[`&.${tableCellClasses.body}`]: {
		fontSize: 12,
		fontWeight: 400,
		color: '#000000',
		padding: '10px',
	},
}));

export const StyledTableRow = styled(TableRow)(({ theme }) => ({
	'&:nth-of-type(odd)': {
		background:
			'linear-gradient(0deg, rgba(255, 255, 255, 0.9), rgba(255, 255, 255, 0.9)), #C2C9D1',
	},
	// hide last border
	'&:last-child td, &:last-child th': {
		border: 0,
	},
}));

export default Resident360Dashboard;
